import React, { useState, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, API_BASE, fmtUSD } from "@/lib/api";
import { toast } from "sonner";
import {
  FolderPlus, Folder, Trash2, Star, Play, Search, Rocket, Lock, X, Video as VideoIcon, Pin
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import VideoPlayer from "@/components/VideoPlayer";
import ReplayTabs from "@/components/ReplayTabs";

// Video library: all Pro clips, organized into folders with HTML5 drag-and-drop.
// - Left rail: All / Starred / Unfiled + user folders (with clip counts)
// - Main grid: clip thumbnails (poster frame). Drag onto a folder to file it.
// - Click a clip → opens video player modal.

const UNFILED = "__unfiled__";
const ALL = "__all__";
const STARRED = "__starred__";

export default function Library() {
  const navigate = useNavigate();
  const qc = useQueryClient();

  const { data: billing } = useQuery({
    queryKey: ["billing-status"],
    queryFn: async () => (await api.get("/billing/status")).data,
  });
  const isPro = billing?.plan_tier === "pro";

  const [activeFolder, setActiveFolder] = useState(ALL);
  const [openClip, setOpenClip] = useState(null);
  const [q, setQ] = useState("");
  const [dropHover, setDropHover] = useState(null);

  const { data: folders = [] } = useQuery({
    queryKey: ["video-folders"],
    queryFn: async () => (await api.get("/video/folders")).data,
    enabled: isPro,
  });

  const listParams = useMemo(() => {
    if (activeFolder === ALL) return {};
    if (activeFolder === STARRED) return { starred: true };
    if (activeFolder === UNFILED) return { folder_id: "unfiled" };
    return { folder_id: activeFolder };
  }, [activeFolder]);

  const { data: clips = [] } = useQuery({
    queryKey: ["video-clips", "library", listParams],
    queryFn: async () => (await api.get("/video/clips", { params: listParams })).data,
    enabled: isPro,
  });

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return clips;
    return clips.filter((c) =>
      (c.symbol || "").toLowerCase().includes(needle)
      || (c.side || "").toLowerCase().includes(needle)
      || (c.date || "").includes(needle)
    );
  }, [clips, q]);

  const createFolder = async () => {
    const name = window.prompt("Folder name?");
    if (!name?.trim()) return;
    try {
      await api.post("/video/folders", { name: name.trim() });
      qc.invalidateQueries({ queryKey: ["video-folders"] });
      toast.success("Folder created");
    } catch (e) { toast.error("Create failed"); }
  };

  const deleteFolder = async (folder) => {
    if (!window.confirm(`Delete folder "${folder.name}"? Its clips will be unfiled (not deleted).`)) return;
    try {
      await api.delete(`/video/folders/${folder.id}`);
      qc.invalidateQueries({ queryKey: ["video-folders"] });
      qc.invalidateQueries({ queryKey: ["video-clips"] });
      if (activeFolder === folder.id) setActiveFolder(ALL);
      toast.success("Folder deleted");
    } catch (e) { toast.error("Delete failed"); }
  };

  const moveClip = async (clipId, folderId) => {
    try {
      await api.patch(`/video/clips/${clipId}`, { folder_id: folderId });
      qc.invalidateQueries({ queryKey: ["video-clips"] });
      qc.invalidateQueries({ queryKey: ["video-folders"] });
      toast.success(folderId ? "Moved" : "Unfiled");
    } catch (e) { toast.error("Move failed"); }
  };

  // Pin / unpin the given clip as the Dashboard "favorite clip". Backend enforces
  // single-pin-per-user (unpins any previously-pinned clip automatically).
  const togglePin = async (clip) => {
    try {
      const next = !clip.is_dashboard_clip;
      const updated = (await api.patch(`/video/clips/${clip.id}`, { is_dashboard_clip: next })).data;
      qc.invalidateQueries({ queryKey: ["video-clips"] });
      qc.invalidateQueries({ queryKey: ["dashboard-clip"] });
      // Update the modal's local state so the pin button flips instantly.
      setOpenClip((prev) => (prev && prev.id === clip.id ? { ...prev, ...updated } : prev));
      toast.success(next ? "Pinned to Dashboard" : "Unpinned from Dashboard");
    } catch (e) { toast.error("Pin failed"); }
  };

  if (!billing) return <div className="p-8" style={{ color: "var(--fg-3)" }}>Loading…</div>;
  if (!isPro) {
    return (
      <div className="p-8 max-w-3xl mx-auto">
        <div className="flex items-center gap-2 mb-2">
          <Folder className="h-5 w-5" strokeWidth={1.5} style={{ color: "var(--accent)" }} />
          <div className="label-eyebrow" style={{ color: "var(--accent)" }}>LIBRARY · PRO</div>
        </div>
        <h1 className="font-heading text-4xl font-semibold tracking-tight mb-4">Organize your tape library</h1>
        <p className="text-base mb-8" style={{ color: "var(--fg-2)" }}>
          Group your video replays into folders like &ldquo;A+ Setups&rdquo;, &ldquo;Rule Breaks&rdquo;, or &ldquo;Reviewing Later&rdquo;.
          Drag clips between folders. Star the ones you want to keep forever.
        </p>
        <div className="p-8" style={{ background: "linear-gradient(180deg, rgba(168,85,247,0.06), var(--surface) 60%)", border: "1px solid rgba(168,85,247,0.35)", borderRadius: 16 }}>
          <button
            onClick={() => navigate("/pricing")}
            className="px-5 py-3 text-sm font-semibold tracking-wide flex items-center gap-2"
            style={{ background: "var(--accent)", color: "white", borderRadius: 10, boxShadow: "0 4px 24px rgba(168,85,247,0.4)" }}
            data-testid="upgrade-cta-library"
          >
            <Rocket className="h-4 w-4" strokeWidth={2} /> UPGRADE TO PRO
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <ReplayTabs />
      <div className="p-8 flex gap-6" style={{ minHeight: "calc(100vh - 40px)" }}>
      {/* Left rail: folders */}
      <aside className="shrink-0 w-64">
        <div className="flex items-center justify-between mb-3">
          <div className="label-eyebrow" style={{ color: "var(--fg-3)" }}>LIBRARY</div>
          <button onClick={createFolder} title="New folder" data-testid="new-folder-btn"
            className="p-1 rounded hover:bg-white/5" style={{ color: "var(--fg-3)" }}>
            <FolderPlus className="h-4 w-4" strokeWidth={1.5} />
          </button>
        </div>
        <div className="space-y-1">
          <FolderRow label="All clips" testid={`folder-${ALL}`} icon={VideoIcon} active={activeFolder === ALL} onClick={() => setActiveFolder(ALL)} />
          <FolderRow label="Starred" testid={`folder-${STARRED}`} icon={Star} iconColor="#fbbf24" active={activeFolder === STARRED} onClick={() => setActiveFolder(STARRED)} />
          <FolderRow
            label="Unfiled"
            testid={`folder-${UNFILED}`}
            icon={Folder}
            active={activeFolder === UNFILED}
            onClick={() => setActiveFolder(UNFILED)}
            onDrop={(clipId) => moveClip(clipId, null)}
            dropActive={dropHover === UNFILED}
            onDragOver={(e) => { e.preventDefault(); setDropHover(UNFILED); }}
            onDragLeave={() => setDropHover(null)}
          />
          <div className="pt-2 pb-1 label-eyebrow" style={{ color: "var(--fg-3)" }}>FOLDERS</div>
          {folders.length === 0 && (
            <div className="text-xs px-3 py-2" style={{ color: "var(--fg-3)" }}>None yet. Create one →</div>
          )}
          {folders.map((f) => (
            <FolderRow
              key={f.id}
              label={f.name}
              testid={`folder-${f.id}`}
              icon={Folder}
              iconColor={f.color}
              active={activeFolder === f.id}
              count={f.clip_count}
              onClick={() => setActiveFolder(f.id)}
              onDrop={(clipId) => moveClip(clipId, f.id)}
              dropActive={dropHover === f.id}
              onDragOver={(e) => { e.preventDefault(); setDropHover(f.id); }}
              onDragLeave={() => setDropHover(null)}
              onDelete={() => deleteFolder(f)}
            />
          ))}
        </div>
      </aside>

      {/* Main grid */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-3 mb-6">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4" strokeWidth={1.5} style={{ color: "var(--fg-3)" }} />
            <input
              type="text" value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Search by symbol, side, or date…"
              data-testid="library-search"
              className="w-full pl-10 pr-3 py-2.5 text-sm focus:outline-none"
              style={{ background: "var(--surface)", border: "1px solid var(--border)", color: "var(--fg)", borderRadius: 10 }}
            />
          </div>
          <div className="text-xs font-mono-num" style={{ color: "var(--fg-3)" }}>{filtered.length} clip{filtered.length === 1 ? "" : "s"}</div>
        </div>

        {filtered.length === 0 ? (
          <div className="p-8 text-center" style={{ background: "var(--surface)", border: "1px dashed var(--border)", borderRadius: 12, color: "var(--fg-3)" }}>
            No clips here. {activeFolder === ALL && "Head to /replay to upload your OBS recording."}
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {filtered.map((c) => (
              <ClipCard key={c.id} clip={c} onClick={() => setOpenClip(c)} />
            ))}
          </div>
        )}
      </div>

      {/* Playback modal */}
      {openClip && (
        <div
          className="fixed inset-0 z-40 flex items-center justify-center p-6"
          style={{ background: "rgba(0,0,0,0.85)" }}
          onClick={() => setOpenClip(null)}
        >
          <div onClick={(e) => e.stopPropagation()} className="max-w-4xl w-full" data-testid="clip-modal">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <span className="font-heading text-lg font-semibold">{openClip.symbol}</span>
                <span className="font-mono-num text-sm" style={{ color: (openClip.pnl || 0) >= 0 ? "var(--green)" : "var(--loss)" }}>{fmtUSD(openClip.pnl)}</span>
                <span className="text-xs" style={{ color: "var(--fg-3)" }}>{openClip.date}</span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => togglePin(openClip)}
                  data-testid="clip-modal-pin-btn"
                  title={openClip.is_dashboard_clip ? "Unpin from Dashboard" : "Pin to Dashboard"}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs transition-colors"
                  style={{
                    background: openClip.is_dashboard_clip ? "var(--accent-soft)" : "transparent",
                    color: openClip.is_dashboard_clip ? "var(--accent)" : "var(--fg-2)",
                    border: `1px solid ${openClip.is_dashboard_clip ? "rgba(168,85,247,0.45)" : "var(--border)"}`,
                    borderRadius: 8,
                  }}
                >
                  <Pin className="h-3.5 w-3.5" strokeWidth={1.5} fill={openClip.is_dashboard_clip ? "currentColor" : "none"} />
                  {openClip.is_dashboard_clip ? "Pinned to Dashboard" : "Pin to Dashboard"}
                </button>
                <button onClick={() => setOpenClip(null)} style={{ color: "var(--fg-3)" }}><X className="h-4 w-4" /></button>
              </div>
            </div>
            <VideoPlayer
              clip={openClip}
              streamUrl={`${API_BASE}/video/clips/${openClip.id}/stream?token=${encodeURIComponent(localStorage.getItem("edge_token") || "")}`}
            />
          </div>
        </div>
      )}
      </div>
    </div>
  );
}
const FolderRow = ({ label, icon: Icon, iconColor, count, active, testid, onClick, onDrop, dropActive, onDragOver, onDragLeave, onDelete }) => (
  <div
    className="group relative"
    onDragOver={onDragOver}
    onDragLeave={onDragLeave}
    onDrop={(e) => {
      e.preventDefault();
      const clipId = e.dataTransfer.getData("clipId");
      if (clipId && onDrop) onDrop(clipId);
      onDragLeave && onDragLeave();
    }}
  >
    <button
      onClick={onClick}
      data-testid={testid}
      className="w-full flex items-center gap-2 px-3 py-2 text-sm transition-all text-left"
      style={{
        background: active ? "var(--accent-soft)" : dropActive ? "rgba(168,85,247,0.14)" : "transparent",
        color: active ? "var(--accent)" : "var(--fg-2)",
        border: dropActive ? "1px dashed var(--accent)" : "1px solid transparent",
        borderRadius: 8,
      }}
    >
      <Icon className="h-3.5 w-3.5 shrink-0" strokeWidth={1.5} style={{ color: iconColor || (active ? "var(--accent)" : "var(--fg-3)") }} />
      <span className="flex-1 truncate">{label}</span>
      {typeof count === "number" && (
        <span className="text-[10px] font-mono-num px-1.5 py-0.5" style={{ background: "var(--surface2)", color: "var(--fg-3)", borderRadius: 4 }}>{count}</span>
      )}
    </button>
    {onDelete && (
      <button
        onClick={onDelete}
        className="absolute right-1 top-1/2 -translate-y-1/2 p-1 opacity-0 group-hover:opacity-100 hover:text-rose-400 transition-opacity"
        style={{ color: "var(--fg-3)" }}
        data-testid={`delete-${testid}`}
      >
        <Trash2 className="h-3 w-3" strokeWidth={1.5} />
      </button>
    )}
  </div>
);

// Bunny-aware prefetch: on hover / in-view, ask the backend which URL to
// use (Bunny HLS or backend fallback). Only prefetch backend URLs — Bunny
// HLS URLs are served by a global CDN and don't benefit from us pre-warming
// them (and each backend prefetch hits our container's storage, so warming
// 20 clips at once used to saturate the pool and make everything slower).
const prefetchedIds = new Set();
const prefetchClip = async (clipId) => {
  if (!clipId || prefetchedIds.has(clipId)) return;
  prefetchedIds.add(clipId);
  try {
    const r = await api.get(`/video/clips/${clipId}/playback`);
    const d = r?.data || {};
    // If Bunny is ready, do NOTHING — the CDN handles cache-warming natively.
    // The HLS playlist file itself is <10KB and loads instantly on click.
    if (d.source === "bunny" && d.hls) return;

    // Backend fallback path: warm the LRU cache + prime the browser HTTP cache
    // with the first 512KB (moov atom + a few frames) so playback starts fast.
    const token = localStorage.getItem("edge_token") || "";
    api.get(`/video/clips/${clipId}`).catch(() => {});
    try {
      fetch(
        `${API_BASE}/video/clips/${clipId}/stream?token=${encodeURIComponent(token)}`,
        { headers: { Range: "bytes=0-524287" }, credentials: "omit" },
      ).catch(() => {});
    } catch (_e) { /* ignore */ }
  } catch (_e) {
    // /playback failed — just skip the prefetch. Click-to-play still works.
  }
};

const ClipCard = ({ clip, onClick }) => {
  const hoverTimer = React.useRef(null);
  const cardRef = React.useRef(null);

  const startPrefetch = () => {
    // Small delay so we don't fire on rapid scrolls / mouse-through
    if (hoverTimer.current) return;
    hoverTimer.current = setTimeout(() => prefetchClip(clip.id), 120);
  };
  const cancelPrefetch = () => {
    if (hoverTimer.current) { clearTimeout(hoverTimer.current); hoverTimer.current = null; }
  };

  // Viewport-aware prefetch: warm each clip's cache when it scrolls into view.
  // Uses IntersectionObserver with a generous rootMargin so we warm the row
  // BEFORE it's visually visible — by the time the user finishes scrolling
  // and clicks, the clip is already halfway loaded.
  React.useEffect(() => {
    const el = cardRef.current;
    if (!el || !("IntersectionObserver" in window)) return;
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            prefetchClip(clip.id);
            io.disconnect();  // One-shot per card
            break;
          }
        }
      },
      { rootMargin: "300px 0px" },  // warm ~300px before the card enters view
    );
    io.observe(el);
    return () => io.disconnect();
  }, [clip.id]);

  return (
  <div
    ref={cardRef}
    draggable
    onDragStart={(e) => e.dataTransfer.setData("clipId", clip.id)}
    onMouseEnter={startPrefetch}
    onMouseLeave={cancelPrefetch}
    // mousedown fires ~100ms before click — buys the backend a head-start warming
    // the cache before the modal even opens and the <video> element mounts.
    onMouseDown={() => prefetchClip(clip.id)}
    onClick={onClick}
    data-testid={`clip-card-${clip.id}`}
    className="group cursor-pointer transition-transform hover:-translate-y-0.5"
    style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 10, overflow: "hidden" }}
  >
    <div className="relative" style={{ aspectRatio: "16 / 9", background: "black" }}>
      {/* Cached first-frame thumbnail (if we snapped one previously) */}
      {(() => {
        try {
          const poster = localStorage.getItem(`tg_poster_${clip.id}`);
          if (poster) return <img src={poster} alt="" className="absolute inset-0 w-full h-full object-cover" style={{ opacity: 0.85 }} />;
        } catch { /* ignore */ }
        return null;
      })()}
      <div className="absolute inset-0 flex items-center justify-center opacity-70 group-hover:opacity-100 transition-opacity">
        <Play className="h-8 w-8 text-white/70" fill="white" strokeWidth={0} />
      </div>
      {clip.starred && (
        <div className="absolute top-2 left-2">
          <Star className="h-3.5 w-3.5" fill="#fbbf24" stroke="#fbbf24" />
        </div>
      )}
      {clip.is_dashboard_clip && (
        <div
          className="absolute top-2 right-2 flex items-center gap-1 px-1.5 py-0.5"
          style={{ background: "rgba(168,85,247,0.9)", borderRadius: 4 }}
          title="Pinned to Dashboard"
        >
          <Pin className="h-2.5 w-2.5" strokeWidth={2} fill="white" stroke="white" />
          <span className="text-[9px] font-bold tracking-wider text-white">PINNED</span>
        </div>
      )}
      <div className="absolute bottom-2 right-2 px-1.5 py-0.5 text-[10px] font-mono-num" style={{ background: "rgba(0,0,0,0.7)", color: "white", borderRadius: 3 }}>
        {Math.round(clip.duration_seconds || 0)}s
      </div>
    </div>
    <div className="p-2.5">
      <div className="flex items-center justify-between text-xs">
        <span className="font-semibold" style={{ color: "var(--fg)" }}>{clip.symbol}</span>
        <span className="font-mono-num" style={{ color: (clip.pnl || 0) >= 0 ? "var(--green)" : "var(--loss)" }}>{fmtUSD(clip.pnl)}</span>
      </div>
      <div className="text-[10px] mt-1 font-mono-num" style={{ color: "var(--fg-3)" }}>
        {clip.date} · {clip.side?.toUpperCase() || ""}
      </div>
    </div>
  </div>
  );
};
